import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import {
  basisPointsFromPercent,
  PAYOUT_METHODS,
  SERVICE_ITEM_CATEGORIES,
  type PayoutMethod,
  type ServiceItemCategory,
} from '@wodi/domain';
import type { RevenueShareRuleView, RevenueShareStatementView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  MoneyField,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  TextField,
} from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import styles from '../accounting/accounting.module.css';
import {
  basisWords,
  dayOffsetIso,
  NotPermitted,
  OfflineList,
  PAYOUT_WORDS,
  periodText,
  RevenueShareTabs,
  StatementBody,
  StatementTag,
  todayIso,
} from '../accounting/common.tsx';
import { ServicePicker, type PickedService } from '../accounting/service-picker.tsx';
import {
  createRevenueShareRule,
  decideStatement,
  endRevenueShareRule,
  myStatementQuery,
  myStatementsQuery,
  practitionersForShareQuery,
  prepareStatement,
  putRevenueShareSettings,
  recordPayout,
  revenueShareKeys,
  revenueShareRulesQuery,
  revenueShareSettingsQuery,
  statementQuery,
  statementsQuery,
} from '../api/accounting.ts';
import { can, sessionQuery } from '../api/session.ts';
import { money, moneyColumn } from '../billing/format.ts';
import { CATEGORY_LABELS } from '../finance/labels.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatIsoDate } from '../patients/format.ts';
import { LoadFailed, ReasonDialog } from '../procurement/common.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// Doctor revenue share (docs/plans/phase3-accounting-and-revenue-share.md §6): statements,
// rules and the facility's basis for the finance office, and each practitioner's own
// statements under My earnings. Statements name services and dates, never patients.

function Frame({ children }: { children: ReactNode }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null || !can(session, 'revenue_share.read'))
    return (
      <>
        <PageHeader title="Revenue share" />
        <NotPermitted what="revenue share" />
      </>
    );
  return (
    <div className={styles.page}>
      <PageHeader
        title="Revenue share"
        description="What practitioners earn from the services they provide. Statements are approved by a second person; payouts are made outside Wodi."
      />
      <RevenueShareTabs />
      <OfflineList />
      {children}
    </div>
  );
}

function usePermission(permission: Parameters<typeof can>[1]): boolean {
  const { data: session } = useSuspenseQuery(sessionQuery);
  return session !== null && can(session, permission);
}

// --- Statements -------------------------------------------------------------------------------

function PrepareSection() {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const practitioners = useQuery(practitionersForShareQuery);
  const today = todayIso();
  const [practitioner, setPractitioner] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState<string | null>(today);
  const [requestId] = useState(() => crypto.randomUUID());
  const prepare = useMutation({
    mutationFn: () =>
      prepareStatement({
        practitionerUserId: practitioner ?? '',
        periodFrom: from ?? '',
        periodTo: to ?? '',
        clientRequestId: requestId,
      }),
    onSuccess: async (statement) => {
      await queryClient.invalidateQueries({ queryKey: revenueShareKeys.all });
      toasts.show({ title: 'Statement prepared', description: statement.number });
      await navigate({
        to: '/revenue-share/statements/$statementId',
        params: { statementId: statement.id },
      });
    },
  });
  return (
    <Section
      title="Prepare a statement"
      description="Everything earned or reversed up to the last day that no statement carries yet, including earlier days."
    >
      <div className={styles.toolbar}>
        <Select
          label="Practitioner"
          width="md"
          options={(practitioners.data ?? []).map((p) => ({ id: p.userId, label: p.displayName }))}
          value={practitioner}
          onChange={(key) => {
            setPractitioner(key === null ? null : String(key));
          }}
          {...(practitioners.isError ? { errorMessage: "Practitioners couldn't be loaded." } : {})}
        />
        <DateField label="From" value={from} maxValue={today} onChange={setFrom} />
        <DateField label="To" value={to} maxValue={today} onChange={setTo} />
        <Button
          variant="primary"
          isDisabled={!online || practitioner === null || from === null || to === null}
          isPending={prepare.isPending}
          pendingLabel="Preparing…"
          onPress={() => {
            prepare.mutate();
          }}
        >
          Prepare statement
        </Button>
      </div>
      {prepare.isError ? (
        <Notice tone="error">
          {errorText(prepare.error, "The statement wasn't prepared. Try again.")}
        </Notice>
      ) : null}
    </Section>
  );
}

function StatementsContent() {
  const canPrepare = usePermission('revenue_share.prepare');
  const statements = useQuery(statementsQuery('all'));
  return (
    <>
      {canPrepare ? <PrepareSection /> : null}
      <Section title="Statements">
        {statements.isPending ? (
          <LoadingBlock label="Loading statements" rows={4} />
        ) : statements.isError ? (
          <LoadFailed
            error={statements.error}
            fallback="The statements couldn't be loaded."
            onRetry={() => void statements.refetch()}
          />
        ) : (
          <TableScroll>
            <Table flat aria-label="Revenue share statements">
              <TableHeader>
                <Column isRowHeader>Statement</Column>
                <Column>Practitioner</Column>
                <Column>Period</Column>
                <Column numeric>Earned (KES)</Column>
                <Column numeric>Adjustments (KES)</Column>
                <Column numeric>Payable (KES)</Column>
                <Column numeric>Paid (KES)</Column>
                <Column>Status</Column>
              </TableHeader>
              <TableBody items={statements.data} renderEmptyState={() => 'No statements yet.'}>
                {(s) => (
                  <Row id={s.id}>
                    <Cell>
                      <Link
                        to="/revenue-share/statements/$statementId"
                        params={{ statementId: s.id }}
                      >
                        <span className="wodi-id">{s.number}</span>
                      </Link>
                    </Cell>
                    <Cell>{s.practitioner.displayName}</Cell>
                    <Cell>{periodText(s.periodFrom, s.periodTo)}</Cell>
                    <Cell numeric>{moneyColumn(s.earned)}</Cell>
                    <Cell numeric>{moneyColumn(s.adjustments)}</Cell>
                    <Cell numeric>{moneyColumn(s.payable)}</Cell>
                    <Cell numeric>{moneyColumn(s.paid)}</Cell>
                    <Cell>
                      <StatementTag status={s.status} />
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        )}
      </Section>
    </>
  );
}

export function RevenueShareStatementsPage() {
  return (
    <Frame>
      <StatementsContent />
    </Frame>
  );
}

// --- One statement ------------------------------------------------------------------------------

function PayoutDialog({
  statement,
  onClose,
}: {
  statement: RevenueShareStatementView;
  onClose: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const left = BigInt(statement.payable.cents) - BigInt(statement.paid.cents);
  const [cents, setCents] = useState<bigint | null>(left);
  const [method, setMethod] = useState<PayoutMethod>('bank_transfer');
  const [reference, setReference] = useState('');
  const [paidOn, setPaidOn] = useState<string | null>(todayIso());
  const [requestId] = useState(() => crypto.randomUUID());
  const record = useMutation({
    mutationFn: () =>
      recordPayout(statement.id, {
        method,
        reference: reference.trim() === '' ? null : reference.trim(),
        paidOn: paidOn ?? '',
        amount: { currency: 'KES', cents: (cents ?? 0n).toString() },
        clientRequestId: requestId,
      }),
    onSuccess: async (result) => {
      queryClient.setQueryData(statementQuery(statement.id).queryKey, result);
      await queryClient.invalidateQueries({ queryKey: revenueShareKeys.all });
      toasts.show({ title: 'Payout recorded', description: result.number });
      onClose();
    },
  });
  const needsReference = method !== 'cash';
  return (
    <Dialog
      title={`Record a payout: ${statement.number}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p
        className={styles.meta}
      >{`Left to pay: ${money({ currency: 'KES', cents: left.toString() })}. Wodi doesn't send money; record what was paid.`}</p>
      <div className={styles.form}>
        <MoneyField label="Amount" value={cents} onChange={setCents} isRequired />
        <Select
          label="Paid by"
          width="sm"
          options={PAYOUT_METHODS.map((m) => ({ id: m, label: PAYOUT_WORDS[m] }))}
          value={method}
          onChange={(key) => {
            if (key !== null) setMethod(String(key) as PayoutMethod);
          }}
        />
        <TextField
          label={needsReference ? 'Reference' : 'Reference (optional)'}
          description="The M-Pesa code, bank reference or cheque number."
          width="md"
          value={reference}
          onChange={setReference}
          maxLength={64}
        />
        <DateField label="Paid on" value={paidOn} maxValue={todayIso()} onChange={setPaidOn} />
      </div>
      {record.isError ? (
        <Notice tone="error">
          {errorText(record.error, "The payout wasn't recorded. Try again.")}
        </Notice>
      ) : null}
      <DialogActions>
        <Button
          variant="primary"
          isDisabled={
            !online ||
            cents === null ||
            cents <= 0n ||
            paidOn === null ||
            (needsReference && reference.trim() === '')
          }
          isPending={record.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            record.mutate();
          }}
        >
          Record payout
        </Button>
        <Button variant="quiet" onPress={onClose}>
          Back
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type Decision = 'approve' | 'reject' | 'withdraw';
const DECISION_WORDS: Record<Decision, { confirm: string; toast: string }> = {
  approve: { confirm: 'Approve statement', toast: 'Statement approved' },
  reject: { confirm: 'Reject statement', toast: 'Statement rejected' },
  withdraw: { confirm: 'Withdraw statement', toast: 'Statement withdrawn' },
};

function StatementActions({ statement }: { statement: RevenueShareStatementView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const [paying, setPaying] = useState(false);
  const decide = useMutation({
    mutationFn: ({ decision, note }: { decision: Decision; note: string }) =>
      decideStatement(statement.id, decision, note === '' ? null : note),
    onSuccess: async (result, { decision }) => {
      queryClient.setQueryData(statementQuery(statement.id).queryKey, result);
      await queryClient.invalidateQueries({ queryKey: revenueShareKeys.all });
      toasts.show({ title: DECISION_WORDS[decision].toast, description: result.number });
      setDeciding(null);
    },
  });
  const waiting = statement.status === 'awaiting_approval';
  if (!waiting && !statement.canRecordPayout) return null;
  return (
    <Section title="Next step">
      <div className={styles.actions}>
        {statement.canDecide ? (
          <>
            <Button
              variant="primary"
              isDisabled={!online}
              onPress={() => {
                setDeciding('approve');
              }}
            >
              Approve statement
            </Button>
            <Button
              isDisabled={!online}
              onPress={() => {
                setDeciding('reject');
              }}
            >
              Reject
            </Button>
          </>
        ) : waiting ? (
          <span className={styles.meta}>
            Someone with approval rights who didn&apos;t prepare it, and isn&apos;t the
            practitioner, must approve it.
          </span>
        ) : null}
        {statement.canWithdraw ? (
          <Button
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              setDeciding('withdraw');
            }}
          >
            Withdraw statement
          </Button>
        ) : null}
        {statement.canRecordPayout ? (
          <Button
            variant="primary"
            isDisabled={!online}
            onPress={() => {
              setPaying(true);
            }}
          >
            Record payout
          </Button>
        ) : null}
      </div>
      {deciding === null ? null : (
        <ReasonDialog
          title={`${DECISION_WORDS[deciding].confirm}: ${statement.number}`}
          confirmLabel={DECISION_WORDS[deciding].confirm}
          optional={deciding === 'approve'}
          isPending={decide.isPending}
          error={decide.error}
          onConfirm={(note) => {
            decide.mutate({ decision: deciding, note });
          }}
          onClose={() => {
            setDeciding(null);
            decide.reset();
          }}
        />
      )}
      {paying ? (
        <PayoutDialog
          statement={statement}
          onClose={() => {
            setPaying(false);
          }}
        />
      ) : null}
    </Section>
  );
}

const statementRoute = getRouteApi('/_app/revenue-share/statements/$statementId');

function StatementContent() {
  const { statementId } = statementRoute.useParams();
  const statement = useQuery(statementQuery(statementId));
  if (statement.isPending) return <LoadingBlock label="Loading the statement" rows={6} />;
  if (statement.isError)
    return (
      <LoadFailed
        error={statement.error}
        fallback="The statement couldn't be loaded."
        onRetry={() => void statement.refetch()}
      />
    );
  return (
    <>
      <p className={styles.meta}>
        <Link to="/revenue-share">Back to statements</Link>
      </p>
      <StatementActions statement={statement.data} />
      <StatementBody statement={statement.data} />
    </>
  );
}

export function RevenueShareStatementPage() {
  return (
    <Frame>
      <StatementContent />
    </Frame>
  );
}

// --- Rules ------------------------------------------------------------------------------------

function percentText(bp: number): string {
  const whole = Math.trunc(bp / 100);
  const rest = bp % 100;
  return rest === 0
    ? `${String(whole)}%`
    : `${String(whole)}.${String(rest).padStart(2, '0').replace(/0$/, '')}%`;
}

function ruleShare(rule: RevenueShareRuleView): string {
  return rule.method === 'percent'
    ? percentText(rule.rateBasisPoints ?? 0)
    : `${money(rule.fixedAmount ?? { currency: 'KES', cents: '0' })} per unit`;
}

function AddRuleSection() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const practitioners = useQuery(practitionersForShareQuery);
  const [practitioner, setPractitioner] = useState<string | null>(null);
  const [scope, setScope] = useState<'category' | 'service'>('category');
  const [category, setCategory] = useState<ServiceItemCategory>('consultation');
  const [service, setService] = useState<PickedService | null>(null);
  const [method, setMethod] = useState<'percent' | 'fixed'>('percent');
  const [percent, setPercent] = useState('');
  const [fixed, setFixed] = useState<bigint | null>(null);
  const [from, setFrom] = useState<string | null>(todayIso());
  const rate = basisPointsFromPercent(percent);
  const rateOk = rate.ok && rate.value >= 1 && rate.value <= 10_000;
  const add = useMutation({
    mutationFn: () =>
      createRevenueShareRule({
        practitionerUserId: practitioner ?? '',
        serviceItemId: scope === 'service' ? (service?.id ?? null) : null,
        category: scope === 'category' ? category : null,
        method,
        rateBasisPoints: method === 'percent' && rate.ok ? rate.value : null,
        fixedAmount:
          method === 'fixed' && fixed !== null
            ? { currency: 'KES', cents: fixed.toString() }
            : null,
        effectiveFrom: from ?? '',
      }),
    onSuccess: async (rule) => {
      await queryClient.invalidateQueries({ queryKey: revenueShareKeys.rules });
      toasts.show({ title: 'Rule added', description: rule.practitioner.displayName });
      setPercent('');
      setFixed(null);
      setService(null);
    },
  });
  const ready =
    practitioner !== null &&
    from !== null &&
    (scope === 'category' || service !== null) &&
    (method === 'percent' ? rateOk : fixed !== null && fixed > 0n);
  return (
    <Section
      title="Add a rule"
      description="One service or a whole category. A rule for a service wins over its category's."
    >
      <div className={styles.form}>
        <Select
          label="Practitioner"
          width="md"
          options={(practitioners.data ?? []).map((p) => ({ id: p.userId, label: p.displayName }))}
          value={practitioner}
          onChange={(key) => {
            setPractitioner(key === null ? null : String(key));
          }}
        />
        <RadioGroup
          label="Applies to"
          orientation="horizontal"
          options={[
            { id: 'category', label: 'A category' },
            { id: 'service', label: 'One service' },
          ]}
          value={scope}
          onChange={(v) => {
            setScope(v === 'service' ? 'service' : 'category');
          }}
        />
        {scope === 'category' ? (
          <Select
            label="Category"
            width="md"
            options={SERVICE_ITEM_CATEGORIES.map((c) => ({ id: c, label: CATEGORY_LABELS[c] }))}
            value={category}
            onChange={(key) => {
              if (key !== null) setCategory(String(key) as ServiceItemCategory);
            }}
          />
        ) : service === null ? (
          <ServicePicker onSelect={setService} />
        ) : (
          <div className={styles.actions}>
            <span>
              {service.name} <span className="wodi-id">{service.code}</span>
            </span>
            <Button
              dense
              variant="quiet"
              onPress={() => {
                setService(null);
              }}
            >
              Change service
            </Button>
          </div>
        )}
        <RadioGroup
          label="Share"
          orientation="horizontal"
          options={[
            { id: 'percent', label: 'A percentage of the charge' },
            { id: 'fixed', label: 'A fixed amount per unit' },
          ]}
          value={method}
          onChange={(v) => {
            setMethod(v === 'fixed' ? 'fixed' : 'percent');
          }}
        />
        <div className={styles.row}>
          {method === 'percent' ? (
            <TextField
              label="Percentage"
              unit="%"
              width="xs"
              value={percent}
              onChange={setPercent}
              {...(percent !== '' && !rateOk
                ? { errorMessage: 'Enter 0.01 to 100, at most two decimals.' }
                : {})}
            />
          ) : (
            <MoneyField label="Amount per unit" value={fixed} onChange={setFixed} />
          )}
          <DateField label="From" value={from} onChange={setFrom} />
          <Button
            variant="primary"
            isDisabled={!online || !ready}
            isPending={add.isPending}
            pendingLabel="Adding…"
            onPress={() => {
              add.mutate();
            }}
          >
            Add rule
          </Button>
        </div>
      </div>
      {add.isError ? (
        <Notice tone="error">{errorText(add.error, "The rule wasn't added. Try again.")}</Notice>
      ) : null}
    </Section>
  );
}

function EndRuleDialog({ rule, onClose }: { rule: RevenueShareRuleView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [endsFrom, setEndsFrom] = useState<string | null>(dayOffsetIso(1));
  const end = useMutation({
    mutationFn: (reason: string) =>
      endRevenueShareRule(rule.id, { endsFrom: endsFrom ?? '', reason }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: revenueShareKeys.rules });
      toasts.show({ title: 'Rule ended', description: rule.practitioner.displayName });
      onClose();
    },
  });
  return (
    <ReasonDialog
      title={`End this rule for ${rule.practitioner.displayName}`}
      confirmLabel="End rule"
      isPending={end.isPending}
      error={end.error}
      onConfirm={(reason) => {
        end.mutate(reason);
      }}
      onClose={onClose}
    >
      <DateField
        label="No longer applies from"
        value={endsFrom}
        minValue={rule.effectiveFrom}
        onChange={setEndsFrom}
      />
    </ReasonDialog>
  );
}

function RulesContent() {
  const canManage = usePermission('revenue_share.manage');
  const rules = useQuery(revenueShareRulesQuery);
  const [ending, setEnding] = useState<RevenueShareRuleView | null>(null);
  return (
    <>
      {canManage ? <AddRuleSection /> : null}
      <Section title="Rules">
        {rules.isPending ? (
          <LoadingBlock label="Loading rules" rows={4} />
        ) : rules.isError ? (
          <LoadFailed
            error={rules.error}
            fallback="The rules couldn't be loaded."
            onRetry={() => void rules.refetch()}
          />
        ) : (
          <TableScroll>
            <Table flat aria-label="Revenue share rules">
              <TableHeader>
                <Column isRowHeader>Practitioner</Column>
                <Column>Applies to</Column>
                <Column>Share</Column>
                <Column>From</Column>
                <Column>Until</Column>
                <Column>Action</Column>
              </TableHeader>
              <TableBody items={rules.data} renderEmptyState={() => 'No rules yet. Add one above.'}>
                {(r) => (
                  <Row id={r.id}>
                    <Cell>{r.practitioner.displayName}</Cell>
                    <Cell>
                      {r.serviceItem === null ? (
                        `${CATEGORY_LABELS[r.category ?? 'other']} (category)`
                      ) : (
                        <>
                          {r.serviceItem.name} <span className="wodi-id">{r.serviceItem.code}</span>
                        </>
                      )}
                    </Cell>
                    <Cell>{ruleShare(r)}</Cell>
                    <Cell>{formatIsoDate(r.effectiveFrom)}</Cell>
                    <Cell>
                      {r.effectiveTo === null
                        ? 'No end date'
                        : `Before ${formatIsoDate(r.effectiveTo)}`}
                    </Cell>
                    <Cell>
                      {canManage && r.effectiveTo === null ? (
                        <Button
                          dense
                          onPress={() => {
                            setEnding(r);
                          }}
                        >
                          End rule
                        </Button>
                      ) : (
                        (r.endReason ?? '')
                      )}
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        )}
      </Section>
      {ending === null ? null : (
        <EndRuleDialog
          rule={ending}
          onClose={() => {
            setEnding(null);
          }}
        />
      )}
    </>
  );
}

export function RevenueShareRulesPage() {
  return (
    <Frame>
      <RulesContent />
    </Frame>
  );
}

// --- Settings ---------------------------------------------------------------------------------

function SettingsContent() {
  const online = useOnline();
  const canManage = usePermission('revenue_share.manage');
  const queryClient = useQueryClient();
  const settings = useQuery(revenueShareSettingsQuery);
  const [basis, setBasis] = useState<'billed' | 'collected'>('collected');
  const [from, setFrom] = useState<string | null>(dayOffsetIso(1));
  const change = useMutation({
    mutationFn: () => putRevenueShareSettings({ basis, effectiveFrom: from ?? '' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: revenueShareKeys.settings });
      toasts.show({
        title: 'Basis changed',
        description: `${basisWords(basis)} from ${formatIsoDate(from ?? '')}`,
      });
    },
  });
  if (settings.isPending) return <LoadingBlock label="Loading settings" rows={3} />;
  if (settings.isError)
    return (
      <LoadFailed
        error={settings.error}
        fallback="The settings couldn't be loaded."
        onRetry={() => void settings.refetch()}
      />
    );
  return (
    <>
      <Section
        title="What shares are earned on"
        description="Billed revenue earns when the service is charged; collected revenue earns when it is paid. A change applies to services given from its date, never earlier."
      >
        <dl className={styles.facts}>
          <dt>Now</dt>
          <dd>{basisWords(settings.data.current)}</dd>
        </dl>
        <TableScroll>
          <Table flat aria-label="Basis changes">
            <TableHeader>
              <Column isRowHeader>From</Column>
              <Column>Basis</Column>
              <Column>Changed by</Column>
            </TableHeader>
            <TableBody
              items={settings.data.changes.map((c) => ({ ...c, id: c.effectiveFrom }))}
              renderEmptyState={() => 'Billed revenue since the start. No changes yet.'}
            >
              {(c) => (
                <Row id={c.id}>
                  <Cell>{formatIsoDate(c.effectiveFrom)}</Cell>
                  <Cell>{basisWords(c.basis)}</Cell>
                  <Cell>{c.changedBy.displayName}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
      {canManage ? (
        <Section title="Change the basis">
          <div className={styles.form}>
            <RadioGroup
              label="Earn on"
              options={[
                { id: 'billed', label: basisWords('billed') },
                { id: 'collected', label: basisWords('collected') },
              ]}
              value={basis}
              onChange={(v) => {
                setBasis(v === 'billed' ? 'billed' : 'collected');
              }}
            />
            <DateField
              label="From"
              value={from}
              minValue={dayOffsetIso(1)}
              onChange={setFrom}
              description="Tomorrow at the earliest."
            />
            <div className={styles.actions}>
              <Button
                variant="primary"
                isDisabled={!online || from === null}
                isPending={change.isPending}
                pendingLabel="Saving…"
                onPress={() => {
                  change.mutate();
                }}
              >
                Change basis
              </Button>
            </div>
          </div>
          {change.isError ? (
            <Notice tone="error">
              {errorText(change.error, "The basis wasn't changed. Try again.")}
            </Notice>
          ) : null}
        </Section>
      ) : null}
    </>
  );
}

export function RevenueShareSettingsPage() {
  return (
    <Frame>
      <SettingsContent />
    </Frame>
  );
}

// --- My earnings ------------------------------------------------------------------------------

function MineFrame({ children }: { children: ReactNode }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null || !can(session, 'revenue_share.own.read'))
    return (
      <>
        <PageHeader title="My earnings" />
        <NotPermitted what="revenue share statements" />
      </>
    );
  return (
    <div className={styles.page}>
      <PageHeader
        title="My earnings"
        description="Your revenue share statements. Payouts are made by the facility, outside Wodi."
      />
      <OfflineList />
      {children}
    </div>
  );
}

function MyStatementsContent() {
  const mine = useQuery(myStatementsQuery);
  if (mine.isPending) return <LoadingBlock label="Loading your statements" rows={4} />;
  if (mine.isError)
    return (
      <LoadFailed
        error={mine.error}
        fallback="Your statements couldn't be loaded."
        onRetry={() => void mine.refetch()}
      />
    );
  return (
    <Section title="Statements">
      <TableScroll>
        <Table flat aria-label="My statements">
          <TableHeader>
            <Column isRowHeader>Statement</Column>
            <Column>Period</Column>
            <Column numeric>Payable (KES)</Column>
            <Column numeric>Paid (KES)</Column>
            <Column>Status</Column>
          </TableHeader>
          <TableBody
            items={mine.data}
            renderEmptyState={() => 'No statements yet. The finance office prepares them.'}
          >
            {(s) => (
              <Row id={s.id}>
                <Cell>
                  <Link to="/my-earnings/$statementId" params={{ statementId: s.id }}>
                    <span className="wodi-id">{s.number}</span>
                  </Link>
                </Cell>
                <Cell>{periodText(s.periodFrom, s.periodTo)}</Cell>
                <Cell numeric>{moneyColumn(s.payable)}</Cell>
                <Cell numeric>{moneyColumn(s.paid)}</Cell>
                <Cell>
                  <StatementTag status={s.status} />
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </TableScroll>
    </Section>
  );
}

export function MyEarningsPage() {
  return (
    <MineFrame>
      <MyStatementsContent />
    </MineFrame>
  );
}

const myStatementRoute = getRouteApi('/_app/my-earnings/$statementId');

function MyStatementContent() {
  const { statementId } = myStatementRoute.useParams();
  const statement = useQuery(myStatementQuery(statementId));
  if (statement.isPending) return <LoadingBlock label="Loading the statement" rows={6} />;
  if (statement.isError)
    return (
      <LoadFailed
        error={statement.error}
        fallback="The statement couldn't be loaded."
        onRetry={() => void statement.refetch()}
      />
    );
  return (
    <>
      <p className={styles.meta}>
        <Link to="/my-earnings">Back to my statements</Link>
      </p>
      <StatementBody statement={statement.data} />
    </>
  );
}

export function MyStatementPage() {
  return (
    <MineFrame>
      <MyStatementContent />
    </MineFrame>
  );
}
